import { i18n } from "../../translate/i18n";
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketManager } from "../../context/Socket/SocketContext";
import api from "../../services/api";

import {
  XAxis, Tooltip, ResponsiveContainer, BarChart, Bar
} from "recharts";
import Grid from "@material-ui/core/Grid";
import MuiTooltip from "@material-ui/core/Tooltip";
import { useTheme } from "@material-ui/core/styles";
import "./SystemResources.css";

const SystemResources = () => {
  const theme = useTheme();
  const isLight = theme.palette.type === "light";

  const getStatusTranslation = (status) => {
    const key = "system_status_" + status;
    const translated = i18n.t(key);
    // fallback if translation doesn't exist
    if (translated === key) return status;
    return translated;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "CONNECTED": return "#10b981";
      case "DISCONNECTED": return "#ef4444";
      case "OPENING":
      case "connecting": 
      case "PAIRING": return "#3b82f6";
      case "TIMEOUT": return "#f59e0b";
      case "qrcode": return "#8b5cf6";
      default: return "#9ca3af";
    }
  };

  const { user } = useContext(AuthContext);
  const [resources, setResources] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user.super) return;

    let isMounted = true;

    const fetchResources = async () => {
      try {
        const { data } = await api.get("/system/resources");
        if (!isMounted) return;
        
        setResources(data);
        setError(null);
        setHistory(prev => {
          const newHistory = [...prev, {
            time: new Date().toLocaleTimeString(),
            cpu: data.cpu?.usage || 0,
            ram: data.memory?.usagePercent || 0
          }];
          if (newHistory.length > 20) newHistory.shift();
          return newHistory;
        });
      } catch (err) {
        if (!isMounted) return;
        console.error("Error fetching resources:", err);
        setError("Error de conexión con el servidor. Intente más tarde.");
      }
    };

    fetchResources();

    const socket = socketManager.GetSocket();
    socket.on("system-resources", (data) => {
      if (!isMounted) return;
      setResources(data);
      setError(null);
      setHistory(prev => {
        const newHistory = [...prev, {
          time: new Date().toLocaleTimeString(),
          cpu: data.cpu?.usage || 0,
          ram: data.memory?.usagePercent || 0
        }];
        if (newHistory.length > 20) newHistory.shift();
        return newHistory;
      });
    });

    return () => {
      isMounted = false;
      socket.off("system-resources");
    };
  }, [user.super]);

  if (!user.super) {
    return (
      <div className="resources-container" style={{ padding: "80px 20px" }}>
        <h2 style={{ color: "#ef4444", fontSize: "24px" }}>Acceso restringido. Solo administradores principales pueden ver esta sección.</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="resources-container" style={{ padding: "80px 20px" }}>
        <h2 style={{ color: "#ef4444", fontSize: "24px" }}>{error}</h2>
      </div>
    );
  }

  if (!resources) {
    return (
      <div className="resources-container" style={{ padding: "80px 20px" }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div className="loader"></div>
          <h2 style={{ color: "#fff", fontSize: "24px", marginTop: "20px" }}>{i18n.t("system_loading")}</h2>
        </div>
      </div>
    );
  }

  const renderGauge = (value, color, label, details) => {
    const radius = 80;
    const circumference = Math.PI * radius;
    // Stroke dash array controls the visible vs hidden path.
    const strokeDashoffset = circumference - (value / 100) * circumference;

    return (
      <div className="resource-card gauge-card">
        <h3>{label}</h3>
        <div style={{ position: 'relative', width: 200, height: 120, margin: '20px auto 0', display: 'flex', justifyContent: 'center' }}>
          <svg width="200" height="120">
            {/* Background Arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              stroke="#374151"
              strokeWidth="16"
              fill="none"
              strokeLinecap="round"
            />
            {/* Progress Arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              stroke={color}
              strokeWidth="16"
              fill="none"
              strokeLinecap="round"
              style={{
                strokeDasharray: circumference,
                strokeDashoffset: strokeDashoffset,
                transition: "stroke-dashoffset 1s ease-out"
              }}
            />
          </svg>
          <div style={{ position: 'absolute', bottom: '0px', color: color, textShadow: `0 0 15px ${color}90`, fontSize: '36px', fontWeight: '800' }}>
            {value}%
          </div>
        </div>
        <div className="gauge-details" style={{ marginTop: '20px' }}>
          {details}
        </div>
      </div>
    );
  };

  return (
    <div className={`resources-container ${isLight ? "resources-light" : ""}`} style={{ padding: "40px 20px 100px 20px", height: "calc(100vh - 60px)", overflowY: "auto", overflowX: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 30 }}>
        <h2 style={{ color: isLight ? "#0F172A" : "#fff", margin: 0, fontSize: "16px", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 700 }}>{i18n.t("system_title")}</h2>
        <div className="uptime-badge">
          <span className="pulse-dot"></span>
          {i18n.t("system_uptime")} {Math.floor((resources.uptime || 0) / 3600)}h {Math.floor(((resources.uptime || 0) % 3600) / 60)}m
        </div>
      </div>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <div className="resource-card" style={{ padding: "0", height: "800px", overflow: "hidden", borderRadius: "12px", border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.05)" }}>
            <iframe 
              src={window.location.protocol === 'https:' ? `/netdata/?local=true&theme=${isLight ? 'white' : 'slate'}&v=2` : `http://${window.location.hostname}:19999?local=true&theme=${isLight ? 'white' : 'slate'}&v=2`} 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Netdata"
              style={{ display: "block" }}
            />
          </div>
        </Grid>
        {/* Watchdog de Empresas y Conexiones */}
        <Grid item xs={12}>
          <div className="resource-card" style={{ padding: "30px", marginTop: "20px" }}>
            
            <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", width: "100%", marginBottom: "20px", flexWrap: "wrap", gap: "15px" }}>
              <h3 style={{ margin: 0, textTransform: "uppercase", fontSize: "14px", letterSpacing: "0.5px", color: isLight ? "#0F172A" : "#fff" }}>
                {i18n.t("system_watchdog")}
              </h3>
              <div style={{ display: "flex", gap: "10px", alignItems: "center", fontSize: "11px", backgroundColor: isLight ? "#F1F5F9" : "rgba(255,255,255,0.05)", padding: "4px 10px", borderRadius: "10px", flexWrap: "wrap" }}>
                <span style={{ color: "#10b981", fontWeight: "bold" }}>● {getStatusTranslation("CONNECTED")}</span>
                <span style={{ color: "#ef4444", fontWeight: "bold" }}>● {getStatusTranslation("DISCONNECTED")}</span>
                <span style={{ color: "#3b82f6", fontWeight: "bold" }}>● {getStatusTranslation("OPENING")}/{getStatusTranslation("PAIRING")}</span>
                <span style={{ color: "#f59e0b", fontWeight: "bold" }}>● {getStatusTranslation("TIMEOUT")}</span>
              </div>
            </div>

            <Grid container spacing={3}>
              {(resources.connections || []).length === 0 ? (
                 <div style={{ padding: "20px", color: isLight ? "#64748B" : "#9ca3af" }}>{i18n.t("system_no_clients")}</div>
              ) : (
                (resources.connections || []).map((company) => (
                  <Grid item xs={12} sm={6} md={4} key={company.companyId}>
                    <div style={{
                      height: "100%",
                      padding: "20px",
                      borderRadius: "12px",
                      backgroundColor: isLight ? "#F8FAFC" : "rgba(17, 24, 39, 0.4)",
                      border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.05)"
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 15 }}>
                        <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, paddingRight: 10 }}>
                          <strong style={{ color: isLight ? "#0F172A" : "#fff", fontSize: "16px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={company.companyName}>
                            {company.companyName}
                          </strong>
                          <div style={{ display: "flex", gap: "10px", marginTop: "4px", fontSize: "11px", color: isLight ? "#475569" : "#cbd5e1" }}>
                            <span title={i18n.t("system_received_this_month")}>📥 {company.traffic?.incoming || 0} {i18n.t("system_received")}</span>
                            <span title={i18n.t("system_sent_this_month")}>📤 {company.traffic?.outgoing || 0} {i18n.t("system_sent")}</span>
                          </div>
                          
                        </div>
                        <span style={{ fontSize: "12px", color: company.whatsapps.length > 0 ? "#10b981" : "#9ca3af", background: isLight ? "#E2E8F0" : "rgba(255,255,255,0.1)", padding: "4px 8px", borderRadius: "10px" }}>{company.whatsapps.length} {i18n.t("system_connections")}</span>
                      </div>
                          {(() => {
                            const discCount = [0,0,0,0,0];
                            const dayNames = [];
                            company.whatsapps.forEach(w => {
                              if (w.disconnectHistory) {
                                w.disconnectHistory.forEach((h, i) => {
                                  discCount[i] = (discCount[i] || 0) + h.count;
                                  dayNames[i] = h.name;
                                });
                              }
                            });
                            const discData = dayNames.map((n, i) => ({ name: n, cortes: discCount[i] }));
                            const hasDisconnects = discData.some(d => d.cortes > 0);
                            // Translate names dynamically
                            const translatedTrafficHistory = (company.trafficHistory || []).map(t => ({...t, name: i18n.t("system_months_" + t.name)}));
                            const translatedDiscData = discData.map(d => ({...d, name: i18n.t("system_days_" + d.name)}));


                            return (
<div style={{ display: "grid", gridTemplateColumns: hasDisconnects ? "1fr 1px 1fr" : "1fr", width: "100%", height: 120, marginBottom: 15 }}>
  {company.trafficHistory && company.trafficHistory.length > 0 && (
    <div style={{ height: "100%", minWidth: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={translatedTrafficHistory.slice(-6)} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
          <XAxis dataKey="name" stroke="#9ca3af" tickLine={false} axisLine={false} tick={{ fontSize: 9 }} interval={0} />
          <Tooltip 
            contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', borderColor: 'rgba(255,255,255,0.1)', fontSize: '11px', borderRadius: '8px' }}
            itemStyle={{ padding: 0 }}
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
          />
          <Bar dataKey="incoming" name={i18n.t("system_received")} fill="#3b82f6" radius={[4, 4, 0, 0]} />
          <Bar dataKey="outgoing" name={i18n.t("system_sent")} fill="#10b981" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )}
  
  {hasDisconnects && <div style={{ background: "rgba(255,255,255,0.08)", width: "1px" }} />}
  {hasDisconnects && (
    <div style={{ height: "100%", minWidth: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={translatedDiscData.slice(-7)} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
          <XAxis dataKey="name" stroke="#9ca3af" tickLine={false} axisLine={false} tick={{ fontSize: 9 }} interval={0} />
          
          <Tooltip 
            contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', borderColor: 'rgba(255,255,255,0.1)', fontSize: '11px', borderRadius: '8px' }}
            itemStyle={{ padding: 0, color: "#f59e0b" }}
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
          />
          <Bar dataKey="cortes" name={i18n.t("system_cuts")} fill="#f59e0b" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )}
</div>
                            );
                          })()}
                        
                      
                      {company.whatsapps.length === 0 ? (
                        <div style={{ color: "#9ca3af", fontSize: "13px", fontStyle: "italic", textAlign: "center", padding: "10px 0" }}>
                          {i18n.t("system_noConnections")}
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {company.whatsapps.map(w => (
                            <div key={w.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(0,0,0,0.2)", padding: "8px 12px", borderRadius: "8px" }}>
                              <span style={{ color: "#e2e8f0", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, paddingRight: "10px" }} title={w.name}>{w.name}</span>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
                                <span style={{ fontSize: "11px", fontWeight: "bold", color: getStatusColor(w.status || "DISCONNECTED") }}>{getStatusTranslation(w.status || "DISCONNECTED")}</span>
                                {w.retries > 0 ? (
                                  <span style={{ fontSize: "11px", color: "#f59e0b", background: "rgba(245, 158, 11, 0.2)", padding: "2px 6px", borderRadius: "4px" }}>
                                    ⚠️ {w.retries}
                                  </span>
                                ) : (w.todayLogs && w.todayLogs.length > 0) ? (
                                  <MuiTooltip title={`${i18n.t("system_cutsToday")} ${w.todayLogs.join(", ")}`} placement="top">
                                    <span style={{ fontSize: "11px", color: "#f59e0b", background: "rgba(245, 158, 11, 0.2)", padding: "2px 6px", borderRadius: "4px", cursor: "help" }}>
                                      ⚠️ {w.todayLogs.length}
                                    </span>
                                  </MuiTooltip>
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Grid>
                ))
              )}
            </Grid>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default SystemResources;
